<template>
  <div>
    <v-row>
      <v-col cols="12" md="6">
        <gg-card-title class="pb-0 pt-0">
          Manage SSL Certificates
        </gg-card-title>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="pt-0">
        <services-search v-model="search" />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <gg-card class="card-borderless">
          <gg-card-header>
            <span>
            <gg-card-title class="d-inline-flex">
              <h4>My SSL Certificates</h4>
            </gg-card-title>
            </span>
                <span>
              <gg-btn class="ml-2"
                      aria-label="Refresh SSL certificates."
                      :loading="loading || runningQueue"
                      @click="updateSslList">
                <v-icon>mdi-refresh</v-icon>
              </gg-btn>
            </span>
          </gg-card-header>
          <gg-card-text>
            <gg-loading :loading="!servicesPopulated" type="table">
              <v-data-table
                :loading="!servicesPopulated || loading || runningQueue"
                :headers="headers"
                :items="mergedSslList"
                :search="search"
                :footer-props="{ itemsPerPageOptions: this.pagination }"
                item-key="ssl_id"
              >
                <template v-slot:item.domain="{ item }">
                  <div v-if="item.status === 'Active'">
                    <router-link v-if="item.isAuto"
                                 custom v-slot="{ navigate }"
                                 :to="{ name: 'ssl_create',
                                   query: {
                                     d: item.servername,
                                     sid: item.serviceId,
                                     cp: item.cpUsername
                                 }}">
                      <button
                        @click="navigate"
                        @keypress.enter="navigate"
                        role="link"
                        aria-label="Manage Certificate"
                        class="ggs-item-title primary--text">
                        {{ item.domain }}
                      </button>
                    </router-link>
                    <router-link v-else :to="`/ssl/${item.ssl_id}`" custom v-slot="{ navigate }">
                      <button
                        @click="navigate"
                        @keypress.enter="navigate"
                        role="link"
                        aria-label="Manage Certificate"
                        class="ggs-item-title primary--text">
                        {{ item.domain }}
                      </button>
                    </router-link>
                  </div>
                  <div v-else>
                    <a href="#" class="ggs-item-title">{{ item.domain }}</a>
                  </div>
                </template>
                <template v-slot:item.status="{ item }">
                  <v-icon small class="pb-1" :color="statusColor(item.status)">mdi-circle</v-icon>
                  {{ item.status }}
                </template>
                <template v-slot:item.name="{ item }">
                  <div>
                  <span v-if="isAlphaSSL(item.name)">
                    <img src="/static/img/amui/globalsign-logo-eye.png" width="24px" alt="GlobalSign Logo"> GlobalSign
                  </span>
                    <span v-else>
                    <img src="/static/img/amui/le-logo-lockonly.png" width="28px" alt="Let's Encrypt Logo"> Let's Encrypt
                  </span>
                  </div>
                </template>
                <template v-slot:item.renewaldate="{ item }">
                  {{ formatRenewalDate(parseRenewalDate(item.renewaldate)) }}
                </template>
                <template v-slot:item.actions="{ item }">
                  <gg-btn v-if="item.status === 'Active' && item.isAuto"
                          :to="{ name: 'ssl_create',
                           query: {
                             d: item.servername,
                             sid: item.serviceId,
                             cp: item.cpUsername
                           }}">
                    Upgrade
                  </gg-btn>
                  <gg-btn v-else-if="item.status === 'Active'" :to="`/ssl/${item.ssl_id}`">
                    Manage
                  </gg-btn>
                  <gg-btn v-else disabled>
                    Manage
                  </gg-btn>
                </template>
              </v-data-table>
            </gg-loading>
          </gg-card-text>
        </gg-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { fromUnixTime, format } from 'date-fns'
import { statusColor, parseRenewalDate, formatRenewalDate } from '@/helpers/helpers'
import ServicesSearch from 'Components/Services/Hosting/ServicesPage/ServicesSearch'

export default {
  name: 'SslCertificatesList',
  components: {
    ServicesSearch
  },
  data () {
    return {
      headers: [
        {
          text: 'Domain Name',
          value: 'domain',
          sortable: true,
        },
        {
          text: 'Billing Status',
          value: 'status',
          sortable: true,
        },
        {
          text: 'SSL Type',
          value: 'name',
          sortable: true,
          class: 'table-heading-sm'
        },
        {
          text: 'Renewal Date',
          value: 'renewaldate',
          sortable: true,
        },
        {
          text: 'SSL Status',
          value: 'ssl_status',
          sortable: true,
        },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          align: 'center'
        },
      ],
      loading: false,
      search: ''
    }
  },
  computed: {
    ...mapGetters({
      certificates: 'security/certificates', // Let’s Encrypt certificates
      runningQueue: 'security/runningQueue',
      ssls: 'ssls',
      servicesPopulated: 'servicesPopulated',
      services: 'services',
      pagination: 'pagination'
    }),
    resellerServices () {
      return this.services.filter(service => service.groupname === 'Reseller Hosting' && service.status === 'Active')
    },
    sharedServices () {
      return this.services.filter(service => service.groupname === 'Shared Hosting' && service.status === 'Active')
    },
    transformedCertificates () {
      return this.certificates.map(crt => {
        const domain = '*.' + crt.servername
        const upgradeable = !!this.ssls.find(ssl => domain === ssl.domain)
        return {
          isAuto: true,
          domain,
          servername: crt.servername,
          ssl_id: crt.certificate.id,
          ssl_status: 'Installed',
          renewaldate: format(fromUnixTime(crt.certificate.not_after), 'yyyy-MM-dd'),
          name: 'Let’s Encrypt',
          status: 'Active',
          upgradeable,
          serviceId: crt.serviceId,
          cpUsername: crt.cpUsername,
          isReseller: crt.isReseller,
        }
      })
    },
    mergedSslList () {
      return [
        ...this.ssls,
        ...this.transformedCertificates
      ]
    },
  },
  methods: {
    ...mapActions({
      getServices: 'getServices',
      clearSslStore: 'clearSslStore',
      runSharedCertificatesQueue: 'security/runSharedCertificatesQueue',
      runResellerCertificatesQueue: 'security/runResellerCertificatesQueue',
      cpanel_uapi: 'cpanel_uapi',
      reportSuccess: 'reportSuccess',
      reportError: 'reportError',
    }),
    ...mapMutations({
      setCertificates: 'security/SET_CERTIFICATES'
    }),
    statusColor,
    parseRenewalDate,
    formatRenewalDate,
    isAlphaSSL (sslname) {
      return !!sslname.includes('AlphaSSL')
    },
    async updateSslList () {
      this.loading = true
      try {
        const response = await this.getServices({ withDomains: false })
        if (response.result === 'success') {
          this.reportSuccess('Certificates successfully updated')
        }
      } catch (e) {
        this.reportError(e)
      }
      this.loading = false
      this.getCPCertificates()
    },
    async getCPCertificates () {
      this.setCertificates([]) // clear current list
      this.loading = true
      const sharedIds = this.sharedServices.map(service => service.id)
      // const sharedIds = ['477', '193', '478'] // test data
      // this.reportSuccess('Loading test data...')
      try {
        await this.runSharedCertificatesQueue(sharedIds)
      } catch (e) {
        this.reportError(e)
      }
      const resellerIds = this.resellerServices.map(service => service.id)
      try {
        await this.runResellerCertificatesQueue(resellerIds)
      } catch (e) {
        this.reportError(e)
      } finally {
        this.loading = false
      }
    },
  },
  mounted () {
    if (!this.runningQueue && !this.certificates.length) {
      this.getCPCertificates()
    }
  }
}
</script>
